import { range } from 'lodash';
import moment, { Moment, unitOfTime } from 'moment';
import { pipe } from 'ramda';
import { ofNullable, map, orElse } from 'core/maybe';
import { TaskField } from 'stores/fields';
import { Task } from 'stores/tasks';
import { DateField } from '../../fields';
import { TaskUpdateRequest } from '../../use-update-tasks';

export enum ShiftBy {
  Days = 'Days',
  Weeks = 'Weeks',
  Months = 'Months',
}

export const getOptions = (shiftBy: ShiftBy) => {
  switch (shiftBy) {
    case ShiftBy.Weeks:
      return range(1, 9);
    case ShiftBy.Months:
      return range(1, 13);
    default:
      return range(1, 11);
  }
};

export const ShiftByOptions = [ShiftBy.Days, ShiftBy.Weeks, ShiftBy.Months];

export enum Direction {
  Later = 'Later',
  Earlier = 'Earlier',
}

export const DirectionOptions = [Direction.Later, Direction.Earlier];

const shift =
  (count: number, shiftBy: ShiftBy, direction: Direction) =>
  (value: Moment): Moment =>
    value
      .clone()
      .add(
        direction === Direction.Later ? count : -count,
        shiftBy.toLowerCase() as unitOfTime.DurationConstructor,
      );

export const getTransformer =
  (count: number, shiftBy: ShiftBy, direction: Direction) =>
  (dates: DateField<string>[]) =>
  (task: Task): TaskUpdateRequest => {
    const from = (date: DateField<string>) =>
      (task[date.fieldName] ?? null) as string | null;
    const to = pipe(
      from,
      ofNullable,
      map<string, Moment>((str) => moment(str)),
      map(shift(count, shiftBy, direction)),
      map<Moment, string>((m) => m.format('YYYY-MM-DD')),
      orElse<string | null>(null),
    );

    return {
      record: task as Record<string, unknown>,
      updates: dates
        .map((field) => ({
          field: field as TaskField,
          from: from(field),
          to: to(field),
        }))
        .filter((x) => x.from !== x.to),
    };
  };
